import { useState } from "react";
import { HeartHandshake, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";

export default function AskPriceModal({ product, open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!product) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/inquiries", { method: "POST", body: { ...form, product_id: product.id || product.slug } });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={`Ask for price — ${product.title}`}
            className="bg-white rounded-cloud shadow-card max-w-md w-full p-6 sm:p-8 relative"
            initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
            transition={{ type: "spring", damping: 24, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} aria-label="Close" data-testid="ask-price-close" className="absolute top-4 right-4 p-2 rounded-full hover:bg-candy-soft transition-colors duration-200"><X className="w-5 h-5" /></button>
            {sent ? (
              <div className="text-center py-8" data-testid="ask-price-success">
                <HeartHandshake className="w-14 h-14 text-candy mx-auto mb-4" />
                <h3 className="font-display font-semibold text-2xl mb-2">Request sent!</h3>
                <p className="text-muted">Rachel's team will reply with a price for the <strong>{product.title}</strong> soon.</p>
                <button onClick={onClose} data-testid="ask-price-done-button" className="mt-6 px-6 py-3 rounded-full bg-candy text-white font-bold shadow-pink hover:bg-candy-deep transition-colors duration-200">Done</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="ask-price-form">
                <h3 className="font-display font-semibold text-2xl mb-1">Ask for price</h3>
                <p className="text-muted text-sm mb-5">Tell us where to send the price for the <strong>{product.title}</strong>.</p>
                <div className="space-y-3">
                  <input required placeholder="Your name" data-testid="ask-price-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold" />
                  <input required type="email" placeholder="Your email" data-testid="ask-price-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold" />
                  <textarea placeholder="Anything special? (colors, size, occasion…)" data-testid="ask-price-message-input" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold resize-none" />
                </div>
                {error && <p className="text-red-500 text-sm font-bold mt-3" data-testid="ask-price-error">{error}</p>}
                <button disabled={busy} data-testid="ask-price-submit" className="mt-5 w-full py-3.5 rounded-full bg-candy hover:bg-candy-deep disabled:opacity-60 text-white font-bold shadow-pink transition-colors duration-200">
                  {busy ? "Sending…" : "Send my request"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
